import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const myPayslipsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-payslips',
  component: MyPayslipsPage,
});

function MyPayslipsPage(): JSX.Element {
  const [error, setError] = React.useState<string | null>(null);
  const listQuery = useQuery({ queryKey: ['my-payslips'], queryFn: () => api.listMyPayslips() });
  const rows = listQuery.data ?? [];

  const download = async (runId: string, itemId: string, month: string) => {
    try {
      const blob = await api.downloadPayslip(runId, itemId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      setError(toMessage(err));
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">My payslips</h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          One per posted payroll run. Adjustment runs show only the difference.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Download failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Payslips</CardTitle>
          <CardDescription>{rows.length} available</CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isPending ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No payslips yet — they appear once a payroll run is locked or posted.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <li
                  key={r.itemId}
                  className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                >
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className="whitespace-nowrap font-medium">{r.month}</span>
                    {r.kind === 'off_cycle' ? <Badge variant="muted">adjustment</Badge> : null}
                    <Badge variant={r.status === 'posted' ? 'default' : 'muted'}>{r.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="whitespace-nowrap font-mono">{formatInr(paise(r.netPaise))}</span>
                    <Button variant="outline" size="sm" onClick={() => download(r.runId, r.itemId, r.month)}>
                      Download
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
